import { DeletePopup, Icon } from '@components/ui';
import { RichTextHelper } from '@helpers';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import styles from './CommentCell.module.scss';

interface CommentCellProps {
  comment?: string;
  metric?: string;
  onEditComment: () => void;
  onDeleteComment: () => void;
  singleLine?: boolean;
}

export const CommentCell: React.FC<CommentCellProps> = ({
  comment,
  metric,
  onEditComment,
  onDeleteComment,
  singleLine = false,
}) => {
  const hasComment = RichTextHelper.hasPlainText(comment);
  const [isOpen, setIsOpen] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState<'popover' | 'actions' | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const deletePopoverBtnRef = useRef<HTMLButtonElement>(null);
  const deleteActionsBtnRef = useRef<HTMLButtonElement>(null);

  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'preventOverflow', options: { padding: 8 } },
    ],
  });

  useEffect(() => {
    if (!isOpen) {
      setShowDeletePopup(prev => (prev === 'popover' ? null : prev));
      return undefined;
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        referenceElement?.contains(target) ||
        popperElement?.contains(target) ||
        (target as Element).closest?.('[data-delete-popup]')
      )
        return;
      setIsOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, referenceElement, popperElement]);

  if (!hasComment) {
    return (
      <div className={clsx(styles.commentCell, singleLine && styles.commentCellSingle)}>
        <button
          type="button"
          className={styles.addBtn}
          onClick={onEditComment}
        >
          <Icon name="add" />
          <span>Add comment</span>
        </button>
      </div>
    );
  }

  const plainText = RichTextHelper.toPlainText(comment);

  return (
    <div
      ref={setReferenceElement}
      className={clsx(styles.commentCell, singleLine && styles.commentCellSingle, isOpen && styles.commentCellOpen)}
    >
      <p
        className={styles.preview}
        title={singleLine ? plainText : undefined}
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            setIsOpen(true);
          }
        }}
      >
        {plainText}
      </p>

      {isOpen &&
        createPortal(
          <div
            ref={setPopperElement}
            className={styles.popover}
            style={popperStyles.popper}
            {...attributes.popper}
          >
            <div className={styles.popoverHeader}>
              <span className={styles.popoverMetric}>{metric}</span>
              <button
                type="button"
                className={styles.popoverClose}
                aria-label="Close"
                onClick={() => setIsOpen(false)}
              >
                <Icon name="close" />
              </button>
            </div>
            <div className="ql-snow">
              <div
                className={clsx('ql-editor', styles.popoverContent)}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment) }}
              />
            </div>
            <div className={styles.popoverFooter}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => {
                  setIsOpen(false);
                  onEditComment();
                }}
              >
                <Icon name="edit" />
                Edit
              </button>
              <button
                ref={deletePopoverBtnRef}
                type="button"
                className={clsx(styles.linkBtn, styles.danger)}
                onClick={() => setShowDeletePopup('popover')}
              >
                <Icon name="delete" />
                Delete
              </button>
              <DeletePopup
                show={showDeletePopup === 'popover'}
                anchorRef={deletePopoverBtnRef}
                placement="bottom-start"
                onClose={() => setShowDeletePopup(null)}
                onDelete={() => {
                  setIsOpen(false);
                  onDeleteComment();
                }}
              />
            </div>
          </div>,
          document.body,
        )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={onEditComment}
          aria-label="Edit"
          title="Edit"
        >
          <Icon name="edit" />
        </button>
        <button
          ref={deleteActionsBtnRef}
          type="button"
          className={styles.iconBtn}
          onClick={() => setShowDeletePopup('actions')}
          aria-label="Delete"
          title="Delete"
        >
          <Icon name="delete" />
        </button>
        <DeletePopup
          show={showDeletePopup === 'actions'}
          anchorRef={deleteActionsBtnRef}
          placement="top-end"
          onClose={() => setShowDeletePopup(null)}
          onDelete={onDeleteComment}
        />
      </div>
    </div>
  );
};
